import { useState } from 'react';
import { motion } from 'framer-motion';
import { FLEET, CREW } from './data';

// FLEET — very different from a tab block. Vertical "spec sheet" with sticky image
// alternating left/right per yacht. NO grid, NO tabs.
export default function Fleet({ setPage }) {
  const [active, setActive] = useState(0);
  return (
    <div className="bg-[#0A1F2E] text-[#E8DDC7]">
      {/* Page lead */}
      <section className="pt-32 pb-16 max-w-[1440px] mx-auto px-8 lg:px-14 border-b border-[#E8DDC7]/10">
        <p className="font-mono text-[10px] tracking-[0.4em] uppercase text-[#B68F4E] mb-4">— Chapter 02 / The Fleet</p>
        <div className="grid lg:grid-cols-[1.6fr_1fr] gap-8 items-end">
          <h1 className="font-serif text-[56px] lg:text-[120px] leading-[0.88] tracking-[-0.025em]">Three temperaments. <span className="italic text-[#B68F4E]">Twelve hulls</span>.</h1>
          <p className="text-base lg:text-lg text-[#E8DDC7]/65 leading-[1.7] max-w-md">Sail, motor, explorer. We charter only yachts our captains have run for at least three full seasons. The rest we politely decline.</p>
        </div>
      </section>

      {/* Class pickers — segmented, three classes */}
      <div className="border-b border-[#E8DDC7]/10 sticky top-[60px] bg-[#0A1F2E]/95 backdrop-blur-md z-30">
        <div className="max-w-[1440px] mx-auto px-8 lg:px-14 py-4 flex items-center gap-2 overflow-x-auto">
          {FLEET.map((f, i) => (
            <button key={f.name} onClick={() => setActive(i)} data-testid={`yacht-class-${f.class}`}
              className={`group flex items-center gap-3 px-4 py-2 transition-colors whitespace-nowrap font-mono text-[11px] tracking-[0.22em] uppercase ${active === i ? 'bg-[#B68F4E] text-[#0A1F2E]' : 'border border-[#E8DDC7]/15 text-[#E8DDC7]/65 hover:text-[#E8DDC7]'}`}>
              <span className="opacity-70">0{i + 1}</span>
              <span>{f.class}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Spec sheet — alternating layout per yacht */}
      <section className="py-20">
        {FLEET.map((f, i) => (
          <motion.div key={f.name} initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-20%' }}
            className={`mb-32 max-w-[1440px] mx-auto px-8 lg:px-14 grid lg:grid-cols-2 gap-10 lg:gap-20 items-center ${i === active ? 'opacity-100' : 'opacity-50'}`}
            onMouseEnter={() => setActive(i)}>
            <div className={`${i % 2 === 1 ? 'lg:order-2' : ''}`}>
              <img src={f.img} alt={f.name} className="w-full aspect-[5/6] object-cover" loading="lazy" />
            </div>
            <div className={`${i % 2 === 1 ? 'lg:order-1' : ''}`}>
              <p className="font-mono text-[10px] tracking-[0.4em] uppercase text-[#B68F4E] mb-3">— Hull no. 0{i + 1} / {f.class}</p>
              <h2 className="font-serif text-5xl lg:text-7xl leading-[0.95]">{f.name.split('—')[0]}<span className="italic text-[#B68F4E] block">{f.name.split('—')[1]}</span></h2>
              {/* Spec table — data-rich, distinctive vs cards */}
              <table className="font-mono text-[12px] mt-10 w-full max-w-md">
                <tbody>
                  {[['LOA', f.loa], ['Guests', f.guests + ' / ' + Math.ceil(f.guests / 2) + ' cabins'], ['Crew', f.crew], ['Builder', f.builder], ['Year', f.year], ['Rate', f.rate]].map(([k, v]) => (
                    <tr key={k} className="border-b border-[#E8DDC7]/15">
                      <td className="py-3 text-[#E8DDC7]/45 tracking-[0.2em] uppercase w-32">{k}</td>
                      <td className="py-3">{v}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <ul className="mt-8 space-y-3">
                {f.highlights.map(h => (
                  <li key={h} className="flex items-start gap-3 text-sm text-[#E8DDC7]/75">
                    <span className="text-[#B68F4E] mt-1">◆</span>
                    <span dangerouslySetInnerHTML={{ __html: h }} />
                  </li>
                ))}
              </ul>
              <button onClick={() => setPage('charter')} className="mt-10 border-b border-[#B68F4E] text-[#B68F4E] pb-1 font-mono text-[10px] tracking-[0.3em] uppercase hover:text-[#E8DDC7] hover:border-[#E8DDC7]" data-testid={`yacht-fleet-enquire-${i}`}>
                Enquire about {f.name.split('—')[0].trim()} →
              </button>
            </div>
          </motion.div>
        ))}
      </section>

      {/* Crew strip — three portraits, sepia-on-hover removed; mono captions */}
      <section className="bg-[#06151E] border-y border-[#E8DDC7]/10">
        <div className="max-w-[1440px] mx-auto px-8 lg:px-14 py-20">
          <p className="font-mono text-[10px] tracking-[0.4em] uppercase text-[#B68F4E] mb-3">— Three of forty</p>
          <h2 className="font-serif text-3xl lg:text-5xl mb-12">The crew comes <span className="italic text-[#B68F4E]">first</span>.</h2>
          <div className="grid md:grid-cols-3 gap-10">
            {CREW.map(c => (
              <div key={c.name} className="group">
                <div className="aspect-[4/5] overflow-hidden bg-[#0A1F2E]"><img src={c.portrait} alt={c.name} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-1000" loading="lazy" /></div>
                <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#B68F4E] mt-5">{c.role}</p>
                <p className="font-serif text-2xl mt-1">{c.name}</p>
                <p className="font-mono text-[11px] text-[#E8DDC7]/50 mt-1" dangerouslySetInnerHTML={{ __html: c.cred }} />
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
